import Cookies from "js-cookie";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import {
  fetchCurrentAdminNotifications,
  markNotificationsAsReadForAdmin,
} from "@/config/queries/admin/admin-notification-queries";
import { format } from "date-fns";
import { Spinner } from "@/components/ui/spinner/spinner";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import { Button } from "@/components/ui/button/button";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";

function getTimeSinceNotification(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const elapsedMilliseconds = now.getTime() - date.getTime();
  const elapsedSeconds = Math.floor(elapsedMilliseconds / 1000);
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  const elapsedDays = Math.floor(elapsedHours / 24);

  if (elapsedDays > 0) {
    return `${elapsedDays} day${elapsedDays > 1 ? "s" : ""} ago`;
  } else if (elapsedHours > 0) {
    return `${elapsedHours} hour${elapsedHours > 1 ? "s" : ""} ago`;
  } else if (elapsedMinutes > 0) {
    return `${elapsedMinutes} minute${elapsedMinutes > 1 ? "s" : ""} ago`;
  } else {
    return `${elapsedSeconds} second${elapsedSeconds !== 1 ? "s" : ""} ago`;
  }
}

const linkMappings: {
  [key: string]: string;
} = {
  Allowance: "/admin/allowance",
  Announcement: "/admin/announcements",
  Salary: "/admin/salaries",
  "Leave Application": "/admin/leave-applications",
  Todo: "/admin/todos",
  Loan: "/admin/other-loans",
  "Loan Repayment Request": "/admin/other-loans",
  "Attendance Request": "/admin/employee-attendance-requests",
  "Performance Report": "/employee",
  DateSheet: "/admin/salary-timesheets",
};

const NotificationsPageForAdmin = () => {
  const queryClient = useQueryClient();
  const adminKey = Cookies.get("key") as string;

  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  });

  const { data, hasNextPage, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: ["admin-notifications-page"],
    queryFn: async ({ pageParam }) =>
      fetchCurrentAdminNotifications({
        page: pageParam,
        adminKey,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.remaining > 0 ? allPages?.length + 1 : undefined;
    },
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage]);

  const { mutate, isPending } = useMutation({
    mutationFn: markNotificationsAsReadForAdmin(adminKey),
  });

  const markNotificationSeen = async (id: string) => {
    mutate([id], {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["admin-notifications-page"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["admin-notifications"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["admin-unseen-notifications"],
        });

        toast.success("Mark read!");
      },

      onError: (error) => {
        console.log("ERROR WHILE MARKING NOTIFICATION READ", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  };

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">Notifications</h3>
      </div>

      <div className="mt-3 pb-8">
        {data?.pages && data?.pages?.length > 0 ? (
          <div>
            {data?.pages?.[0]?.notifications?.length > 0 ? (
              <div className="flex flex-col relative gap-4">
                {data?.pages?.map((page, index) => {
                  const notifications = page?.notifications;

                  return (
                    <div key={index} className="flex flex-col relative gap-5">
                      {notifications?.map((noti, i) => {
                        const href = linkMappings[noti?.article];

                        return (
                          <div
                            key={i}
                            className="shadow border flex items-center justify-between p-3 rounded-lg"
                          >
                            <div className="flex-1 shrink-0">
                              {href ? (
                                <Link
                                  to={href}
                                  className="text-base font-medium block"
                                >
                                  {noti?.article}
                                </Link>
                              ) : (
                                <div className="text-base font-medium">
                                  {noti?.article}
                                </div>
                              )}
                              {noti?.isSeen && (
                                <span className="text-[11px]">Seen</span>
                              )}
                            </div>
                            <div className="flex-1 shrink-0">
                              <div
                                className="text-sm font-normal"
                                dangerouslySetInnerHTML={{
                                  __html: noti?.notificationTitle,
                                }}
                              ></div>
                            </div>
                            <div className="flex-1 text-right flex justify-end gap-4">
                              <div className="flex flex-col gap-1">
                                <div className="text-[12px] text-popover-foreground">
                                  {getTimeSinceNotification(noti?.createdAt)}
                                </div>
                                <div className="text-[11px]">
                                  {format(
                                    new Date(noti?.createdAt as string),
                                    "PPpp"
                                  )}
                                </div>
                              </div>

                              {!noti?.isSeen && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    markNotificationSeen(noti?._id as string)
                                  }
                                  disabled={isPending}
                                >
                                  Mark as read
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                <div ref={ref} className="h-1 flex-1 p-1" />

                {isFetching && <Spinner className="mb-2"></Spinner>}
              </div>
            ) : (
              <div className="p-4 text-center h-[20rem] flex flex-col items-center justify-center gap-6">
                <div className="relative w-40 h-40 lg:w-[25rem] lg:h-64 shrink-0">
                  <img
                    src="/notification-asset.svg"
                    alt="Notification Draw"
                    className="max-w-full w-full h-full object-cover"
                  />
                </div>
                <h1 className="font-medium text-xl">
                  Nothing to catch up here yet!
                </h1>
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 text-center h-[20rem] flex flex-col items-center justify-center gap-6">
            <div className="relative w-40 h-40 lg:w-[25rem] lg:h-64 shrink-0">
              <img
                src="/notification-asset.svg"
                alt="Notification Draw"
                className="max-w-full w-full h-full object-cover"
              />
            </div>
            <h1 className="font-medium text-xl">
              Nothing to catch up here yet!
            </h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPageForAdmin;
